import React, { useState } from "react";
import axios from "axios";
import { Shield, AlertTriangle, Trash2, Loader2, CheckCircle, X, Lock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PrivacyPanel — DPDP §12 right-to-erasure UI for the customer.
 *
 * Lives in My Account so the data principal can delete their data in 1–2 clicks
 * (RBI/DPDP best practice; saves us from §13(2)(b) "non-compliant access" fines).
 *
 * Flow:
 *   1. Customer clicks "Delete my data" → confirmation modal opens.
 *   2. Customer types DELETE in confirmation field, clicks "Send OTP".
 *   3. We POST /api/customer/phone/send-otp (same Twilio Verify flow as login).
 *   4. Customer enters 6-digit code, clicks "Erase permanently".
 *   5. Backend verifies OTP via Twilio, scrubs PII from leads / bookings / WA history.
 *   6. We show the erasure ID receipt for their records.
 */

const PrivacyPanel = ({ phone }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intro"); // intro | otp | done
  const [confirmText, setConfirmText] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep("intro"); setConfirmText(""); setOtp(""); setError("");
    setSending(false); setErasing(false); setResult(null);
  };

  const close = () => { setOpen(false); reset(); };

  const sendOTP = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      setError("Type DELETE in capital letters to confirm");
      return;
    }
    setError(""); setSending(true);
    try {
      await axios.post(`${API_URL}/api/customer/phone/send-otp`, { phone, force: true });
      setStep("otp");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send OTP — try again in a minute");
    } finally { setSending(false); }
  };

  const erase = async () => {
    if (!otp || otp.length < 4) { setError("Enter the OTP"); return; }
    setError(""); setErasing(true);
    try {
      const r = await axios.post(`${API_URL}/api/dpdp/erase-my-data`, {
        phone, otp, confirm_text: "DELETE",
      });
      setResult(r.data);
      setStep("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erasure failed — please contact privacy@doozygo.com");
    } finally { setErasing(false); }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6" data-testid="privacy-panel">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Shield size={18} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-[#1E1B4B]">Privacy &amp; data control</h3>
            <p className="text-xs text-[#475569] mt-1">
              Per the Digital Personal Data Protection Act 2023, you have the right to access, correct, and erase your personal data.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
              <a href="/legal/privacy" className="text-[#E83E8C] hover:underline font-semibold" data-testid="privacy-policy-link">Privacy policy</a>
              <span className="text-gray-300">·</span>
              <a href="mailto:privacy@doozygo.com" className="text-[#E83E8C] hover:underline font-semibold">Email privacy@doozygo.com</a>
              <span className="text-gray-300">·</span>
              <button
                onClick={() => setOpen(true)}
                className="text-rose-600 hover:text-rose-700 font-semibold inline-flex items-center gap-1"
                data-testid="open-erase-modal-btn"
              >
                <Trash2 size={11} /> Delete my data
              </button>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center p-4" data-testid="erase-modal">
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl">
            <div className="px-5 py-4 bg-rose-50 flex items-center gap-3 border-b border-rose-100">
              <AlertTriangle size={20} className="text-rose-600 flex-shrink-0" />
              <h3 className="text-base font-bold text-rose-900 flex-1">Permanently delete my data</h3>
              <button onClick={close} aria-label="Close" className="p-1 rounded-lg hover:bg-rose-100" data-testid="erase-close-btn">
                <X size={18} className="text-rose-700" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {step === "intro" && (
                <>
                  <div className="text-sm text-[#1E1B4B] space-y-2">
                    <p className="font-semibold">This will scrub your name, email, address, and WhatsApp history from our active systems within seconds.</p>
                    <p className="text-xs text-[#475569] leading-relaxed">
                      We will retain a financial record of any past paid bookings (booking ID, amount, GST) for <strong>8 years</strong> as required by Indian Income-Tax law (§17 of the DPDP Act expressly permits this lawful retention). All other identifying fields will be replaced with <code className="bg-gray-100 px-1 rounded">[ERASED]</code>.
                    </p>
                    <p className="text-xs text-[#475569]">This is irreversible. You can keep using DoozyGo® afterwards but you will appear as a new customer.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5">
                      Type <code className="bg-gray-100 px-1 rounded">DELETE</code> to confirm
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={e => { setConfirmText(e.target.value); setError(""); }}
                      placeholder="DELETE"
                      className="w-full px-3 py-2.5 rounded-lg border-2 border-rose-200 focus:border-rose-500 focus:outline-none text-sm font-mono uppercase"
                      data-testid="erase-confirm-input"
                    />
                  </div>
                  {error && <p className="text-xs text-rose-600 flex items-center gap-1"><AlertTriangle size={11} /> {error}</p>}
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={close} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-[#1E1B4B] text-sm font-semibold hover:bg-gray-200 transition" data-testid="erase-cancel-btn">
                      Cancel
                    </button>
                    <button
                      onClick={sendOTP}
                      disabled={sending || confirmText.trim().toUpperCase() !== "DELETE"}
                      className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                      data-testid="erase-send-otp-btn"
                    >
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                      Send OTP to verify
                    </button>
                  </div>
                </>
              )}

              {step === "otp" && (
                <>
                  <div className="text-sm text-[#1E1B4B] space-y-1.5">
                    <p>We sent a 6-digit OTP to <strong>{phone}</strong>. Enter it below to permanently erase your data.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5">OTP</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otp}
                      onChange={e => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 8)); setError(""); }}
                      placeholder="••••••"
                      className="w-full px-3 py-3 rounded-lg border-2 border-rose-200 focus:border-rose-500 focus:outline-none text-lg font-mono tracking-[0.4em] text-center"
                      data-testid="erase-otp-input"
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-xs text-rose-600 flex items-center gap-1"><AlertTriangle size={11} /> {error}</p>}
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={() => { setStep("intro"); setOtp(""); setError(""); }} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-[#1E1B4B] text-sm font-semibold hover:bg-gray-200 transition" data-testid="erase-back-btn">
                      Back
                    </button>
                    <button
                      onClick={erase}
                      disabled={erasing || otp.length < 4}
                      className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                      data-testid="erase-confirm-btn"
                    >
                      {erasing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      Erase permanently
                    </button>
                  </div>
                </>
              )}

              {step === "done" && result && (
                <>
                  <div className="text-center py-3">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
                      <CheckCircle size={28} className="text-emerald-600" />
                    </div>
                    <p className="text-base font-bold text-[#1E1B4B]">Your data has been erased</p>
                    <p className="text-xs text-[#475569] mt-1">Within seconds. As required by DPDP §12.</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900 space-y-1" data-testid="erase-result">
                    <p><strong>Erasure ID:</strong> <code className="font-mono">{result.erasure_id}</code></p>
                    <p>Records scrubbed: {result.leads_scrubbed} leads, {result.bookings_scrubbed} bookings, {result.wa_scrubbed} WhatsApp messages</p>
                  </div>
                  <p className="text-[11px] text-[#94a3b8] leading-relaxed">
                    Save the erasure ID for your records. If you have any concerns, contact <a href="mailto:privacy@doozygo.com" className="text-[#E83E8C]">privacy@doozygo.com</a>.
                  </p>
                  <button onClick={close} className="w-full py-2.5 rounded-xl bg-[#1E1B4B] text-white text-sm font-bold hover:bg-[#2D2A5F] transition" data-testid="erase-done-btn">
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PrivacyPanel;
